import { CreativesSortingOrder } from "@north-beam/nb-client-side";
import {
  type DateRangeChoiceV3,
  type FixedDateInterval,
  type IGranularity,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
  type ITimeGranularityV2,
  type constants,
} from "@north-beam/nb-common";

import {
  AccountingModeParam,
  ActiveTabParam,
  AttributionWindowParam,
  BreakdownParam,
  CompareDateRangeParam,
  DEFAULT_PAGE_CONFIGURATION,
  DateRangeParam,
  GranularityParam,
  SalesSortingOrderParam,
  SalesSortingParam,
  TimeGranularityParam,
} from "../context";
import { TableStateJsonParam } from "../hooks/use-table-state";
import { type TabId } from "../tabs";
import { type TopLevelBreakdown } from "../types";

const DEFAULT_TABLE_STATE = {
  columnConfigs: [
    { id: "spend", width: 137, showMiniGraph: false },
    { id: "Attributed Rev", width: 158, showMiniGraph: false },
    {
      id: "LTV Attributed Rev__infinity",
      width: 151,
      showMiniGraph: false,
    },
    { id: "ROAS", width: 150, showMiniGraph: true },
    { id: "LTV ROAS__infinity", width: 143, showMiniGraph: true },
    { id: "metaROAS7DClick1DView", width: 197, showMiniGraph: true },
    { id: "Forecasted ROAS__30", width: 173, showMiniGraph: false },
    { id: "Forecasted ROAS__60", width: 170, showMiniGraph: false },
    { id: "Forecasted ROAS__90", width: 174, showMiniGraph: false },
    { id: "Email Signup Rate", width: 171, showMiniGraph: false },
    { id: "percentageNewVisits", width: 120, showMiniGraph: false },
    { id: "CAC", width: 180, showMiniGraph: true },
    { id: "LTV CAC__infinity", width: 120, showMiniGraph: false },
    { id: "ctr", width: 120, showMiniGraph: false },
    { id: "ecpc", width: 120, showMiniGraph: false },
    { id: "cpm", width: 120, showMiniGraph: false },
    { id: "impressions", width: 120, showMiniGraph: false },
    { id: "ECR", width: 120, showMiniGraph: false },
    { id: "LTV ECR__infinity", width: 120, showMiniGraph: false },
    { id: "Transactions", width: 163, showMiniGraph: false },
    { id: "AOV", width: 120, showMiniGraph: false },
    { id: "ecpnv", width: 120, showMiniGraph: false },
    { id: "visits", width: 120, showMiniGraph: false },
    { id: "newVisits", width: 120, showMiniGraph: false },
    {
      id: "LTV Transactions__infinity",
      width: 154,
      showMiniGraph: false,
    },
    { id: "Attributed Rev__ft", width: 278, showMiniGraph: false },
    {
      id: "LTV Attributed Rev__infinity__ft",
      width: 278,
      showMiniGraph: false,
    },
    { id: "ROAS__ft", width: 182, showMiniGraph: false },
    { id: "LTV ROAS__infinity__ft", width: 182, showMiniGraph: false },
    { id: "ECR__ft", width: 173, showMiniGraph: false },
    { id: "LTV ECR__infinity__ft", width: 173, showMiniGraph: false },
    { id: "CAC__ft", width: 173, showMiniGraph: false },
    { id: "LTV CAC__infinity__ft", width: 173, showMiniGraph: false },
    { id: "Transactions__ft", width: 259, showMiniGraph: false },
    {
      id: "LTV Transactions__infinity__ft",
      width: 259,
      showMiniGraph: false,
    },
    { id: "AOV__ft", width: 259, showMiniGraph: false },
    { id: "Attributed Rev__rtn", width: 288, showMiniGraph: false },
    {
      id: "LTV Attributed Rev__infinity__rtn",
      width: 288,
      showMiniGraph: false,
    },
    { id: "ROAS__rtn", width: 192, showMiniGraph: false },
    { id: "LTV ROAS__infinity__rtn", width: 192, showMiniGraph: false },
    { id: "ECR__rtn", width: 182, showMiniGraph: false },
    { id: "LTV ECR__infinity__rtn", width: 182, showMiniGraph: false },
    { id: "CAC__rtn", width: 182, showMiniGraph: false },
    { id: "LTV CAC__infinity__rtn", width: 182, showMiniGraph: false },
    { id: "Transactions__rtn", width: 269, showMiniGraph: false },
    {
      id: "LTV Transactions__infinity__rtn",
      width: 269,
      showMiniGraph: false,
    },
    { id: "AOV__rtn", width: 269, showMiniGraph: false },
  ],
  fixedColumnWidths: { Name: 450, Status: 149 },
};

export const makeSalesReportStateQuery = ({
  attributionModel,
  attributionWindow,
  accountingMode,
  dateRange,
  compareDateRange,
  timeGranularity,
  activeTab,
  granularity,
  filters,
}: {
  attributionModel: string;
  attributionWindow: IMCAttributionWindowDays;
  accountingMode: INBAccountingMode;
  dateRange: FixedDateInterval | DateRangeChoiceV3;
  compareDateRange: constants.CompareDateRange;
  timeGranularity: ITimeGranularityV2;
  granularity: IGranularity;
  activeTab: TabId;
  filters: TopLevelBreakdown[];
}) => {
  const search = new URLSearchParams();

  search.set("attributionModel", attributionModel);
  search.set(
    "attributionWindow",
    AttributionWindowParam.encode(attributionWindow) as string,
  );
  search.set(
    "accountingMode",
    AccountingModeParam.encode(accountingMode) as string,
  );
  search.set("dateRange", DateRangeParam.encode(dateRange) as string);
  search.set(
    "compareDateRange",
    CompareDateRangeParam.encode(compareDateRange) as string,
  );
  search.set(
    "timeGranularity",
    TimeGranularityParam.encode(timeGranularity) as string,
  );
  search.set("granularity", GranularityParam.decode(granularity) as string);
  search.set("activeTab", ActiveTabParam.decode(activeTab));
  search.set("filters", BreakdownParam.encode(filters) as string);
  search.set(
    "sortingOrder",
    SalesSortingOrderParam.encode(CreativesSortingOrder.desc) as string,
  );
  search.set(
    "sorting",
    SalesSortingParam.encode(DEFAULT_PAGE_CONFIGURATION.sorting) as string,
  );
  search.set(
    "tableState",
    TableStateJsonParam.encode(DEFAULT_TABLE_STATE) as string,
  );
  search.set("savedViewId", "-1");

  return search;
};
